export interface HourlyWeather {
  time: string
  temperature: number
  humidity: number
  windSpeed: number
  precipitation: number
  weatherCode: number
}

export interface WeatherBlock {
  label: string
  dateLabel: string
  hours: HourlyWeather[]
  avgTemp: number
  maxWind: number
  avgHumidity: number
  totalPrecip: number
  dominantCode: number
}

export interface WeatherForecastData {
  blocks: WeatherBlock[]
  alerts: WeatherAlert[]
}

export interface WeatherAlert {
  type: 'storm' | 'wind' | 'rain' | 'snow' | 'cold' | 'heat'
  messageKey: string
  messageValue?: string
  message: string // kept for backward compat
  severity: 'warning' | 'danger'
}

// Open-Meteo WMO Weather Codes — returns i18n key
export function getWeatherDescriptionKey(code: number): string {
  if (code === 0) return 'weather.code.0'
  if (code === 1) return 'weather.code.1'
  if (code === 2) return 'weather.code.2'
  if (code === 3) return 'weather.code.3'
  if (code <= 48) return 'weather.code.fog'
  if (code <= 55) return 'weather.code.drizzle'
  if (code <= 57) return 'weather.code.freezingDrizzle'
  if (code === 61) return 'weather.code.lightRain'
  if (code === 63) return 'weather.code.moderateRain'
  if (code === 65) return 'weather.code.heavyRain'
  if (code <= 67) return 'weather.code.freezingRain'
  if (code === 71) return 'weather.code.lightSnow'
  if (code === 73) return 'weather.code.moderateSnow'
  if (code === 75) return 'weather.code.heavySnow'
  if (code === 77) return 'weather.code.snowGrains'
  if (code === 80) return 'weather.code.lightShowers'
  if (code === 81) return 'weather.code.moderateShowers'
  if (code === 82) return 'weather.code.heavyShowers'
  if (code <= 86) return 'weather.code.snowShowers'
  if (code === 95) return 'weather.code.thunderstorm'
  if (code >= 96) return 'weather.code.thunderstormHail'
  return 'weather.code.unknown'
}

// Keep original for backward compatibility (used by climateEngine etc.)
export function getWeatherDescription(code: number): string {
  if (code === 0) return 'Clear sky'
  if (code <= 3)
    return code === 1
      ? 'Mainly clear'
      : code === 2
        ? 'Partly cloudy'
        : 'Overcast'
  if (code <= 48) return 'Fog'
  if (code <= 55) return 'Light drizzle'
  if (code <= 57) return 'Freezing drizzle'
  if (code === 61) return 'Light rain'
  if (code === 63) return 'Moderate rain'
  if (code === 65) return 'Heavy rain'
  if (code <= 67) return 'Freezing rain'
  if (code === 71) return 'Light snow'
  if (code === 73) return 'Moderate snow'
  if (code === 75) return 'Heavy snow'
  if (code === 77) return 'Snow grains'
  if (code === 80) return 'Light showers'
  if (code === 81) return 'Moderate showers'
  if (code === 82) return 'Heavy showers'
  if (code <= 86) return 'Snow showers'
  if (code === 95) return 'Thunderstorm'
  if (code >= 96) return 'Thunderstorm w/ hail'
  return 'Unknown'
}

export function getWeatherEmoji(code: number): string {
  if (code === 0) return '☀️'
  if (code <= 2) return '⛅'
  if (code === 3) return '☁️'
  if (code <= 48) return '🌫️'
  if (code <= 57) return '🌦️'
  if (code <= 67) return '🌧️'
  if (code <= 77) return '🌨️'
  if (code <= 82) return '🌧️'
  if (code <= 86) return '🌨️'
  if (code >= 95) return '⛈️'
  return '🌤️'
}

function getTimeBlockLabel(hour: number): string {
  if (hour >= 6 && hour < 12) return 'Morning'
  if (hour >= 12 && hour < 18) return 'Afternoon'
  if (hour >= 18 && hour < 24) return 'Evening'
  return 'Night'
}

function getBlockStartHour(label: string): number {
  switch (label) {
    case 'Morning':
      return 6
    case 'Afternoon':
      return 12
    case 'Evening':
      return 18
    case 'Night':
      return 0
    default:
      return 0
  }
}

function generateAlerts(blocks: WeatherBlock[]): WeatherAlert[] {
  const alerts: WeatherAlert[] = []
  const allHours = blocks.flatMap((b) => b.hours)

  // Check for thunderstorms
  if (allHours.some((h) => h.weatherCode >= 95)) {
    alerts.push({
      type: 'storm',
      messageKey: 'weather.alert.storm',
      message: 'Thunderstorm activity expected during the race',
      severity: 'danger',
    })
  }

  // Check for heavy rain
  const maxPrecip = Math.max(...allHours.map((h) => h.precipitation))
  if (maxPrecip >= 5) {
    alerts.push({
      type: 'rain',
      messageKey: 'weather.alert.heavyRain',
      messageValue: maxPrecip.toFixed(1),
      message: `Heavy precipitation expected (up to ${maxPrecip.toFixed(1)} mm/h)`,
      severity: 'warning',
    })
  }

  // Check for snow
  if (
    allHours.some(
      (h) =>
        (h.weatherCode >= 71 && h.weatherCode <= 77) ||
        (h.weatherCode >= 85 && h.weatherCode <= 86),
    )
  ) {
    alerts.push({
      type: 'snow',
      messageKey: 'weather.alert.snow',
      message: 'Snowfall expected during the race',
      severity: 'warning',
    })
  }

  // Check for strong wind
  const maxWind = Math.max(...allHours.map((h) => h.windSpeed))
  if (maxWind >= 40) {
    alerts.push({
      type: 'wind',
      messageKey: 'weather.alert.strongWind',
      messageValue: Math.round(maxWind).toString(),
      message: `Strong winds expected (up to ${Math.round(maxWind)} km/h)`,
      severity: 'danger',
    })
  } else if (maxWind >= 25) {
    alerts.push({
      type: 'wind',
      messageKey: 'weather.alert.moderateWind',
      messageValue: Math.round(maxWind).toString(),
      message: `Moderate winds expected (up to ${Math.round(maxWind)} km/h)`,
      severity: 'warning',
    })
  }

  // Check for cold
  const minTemp = Math.min(...allHours.map((h) => h.temperature))
  if (minTemp <= 0) {
    alerts.push({
      type: 'cold',
      messageKey: 'weather.alert.freezing',
      messageValue: minTemp.toFixed(1),
      message: `Freezing temperatures expected (down to ${minTemp.toFixed(1)}°C)`,
      severity: 'danger',
    })
  } else if (minTemp <= 5) {
    alerts.push({
      type: 'cold',
      messageKey: 'weather.alert.cold',
      messageValue: minTemp.toFixed(1),
      message: `Cold conditions expected (down to ${minTemp.toFixed(1)}°C)`,
      severity: 'warning',
    })
  }

  // Check for heat
  const maxTemp = Math.max(...allHours.map((h) => h.temperature))
  if (maxTemp >= 35) {
    alerts.push({
      type: 'heat',
      messageKey: 'weather.alert.extremeHeat',
      messageValue: maxTemp.toFixed(1),
      message: `Extreme heat expected (up to ${maxTemp.toFixed(1)}°C)`,
      severity: 'danger',
    })
  } else if (maxTemp >= 30) {
    alerts.push({
      type: 'heat',
      messageKey: 'weather.alert.hot',
      messageValue: maxTemp.toFixed(1),
      message: `Hot conditions expected (up to ${maxTemp.toFixed(1)}°C)`,
      severity: 'warning',
    })
  }

  return alerts
}

export async function fetchWeatherForecast(
  lat: number,
  lon: number,
  startTime: Date,
  durationHours: number,
): Promise<WeatherForecastData | null> {
  try {
    const endTime = new Date(
      startTime.getTime() + durationHours * 60 * 60 * 1000,
    )
    const startDate = startTime.toISOString().split('T')[0]
    const endDate = endTime.toISOString().split('T')[0]

    const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&hourly=temperature_2m,relative_humidity_2m,wind_speed_10m,precipitation,weather_code&timezone=auto&start_date=${startDate}&end_date=${endDate}`

    const response = await fetch(url)
    if (!response.ok) return null

    const data = await response.json()
    if (!data.hourly) return null

    const startHour = startTime.getHours()
    const endHour = endTime.getHours()
    const startDay = startDate
    const endDay = endDate

    // Parse all hourly data within race window
    const hourlyData: HourlyWeather[] = []
    for (let i = 0; i < data.hourly.time.length; i++) {
      const time = new Date(data.hourly.time[i])
      if (time >= startTime && time <= endTime) {
        hourlyData.push({
          time: data.hourly.time[i],
          temperature: data.hourly.temperature_2m[i],
          humidity: data.hourly.relative_humidity_2m[i],
          windSpeed: data.hourly.wind_speed_10m[i],
          precipitation: data.hourly.precipitation[i],
          weatherCode: data.hourly.weather_code[i],
        })
      }
    }

    if (hourlyData.length === 0) return null

    // Group into time blocks (6h periods)
    const blockMap = new Map<string, HourlyWeather[]>()
    for (const h of hourlyData) {
      const t = new Date(h.time)
      const dayStr = t.toISOString().split('T')[0]
      const hour = t.getHours()
      const label = getTimeBlockLabel(hour)
      const key = `${dayStr}_${label}`
      if (!blockMap.has(key)) blockMap.set(key, [])
      blockMap.get(key)!.push(h)
    }

    const blocks: WeatherBlock[] = Array.from(blockMap.entries()).map(
      ([key, hours]) => {
        const [dayStr, label] = key.split('_')
        const date = new Date(dayStr + 'T12:00:00')
        const dateLabel = date.toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short',
        })

        const avgTemp =
          hours.reduce((s, h) => s + h.temperature, 0) / hours.length
        const maxWind = Math.max(...hours.map((h) => h.windSpeed))
        const avgHumidity =
          hours.reduce((s, h) => s + h.humidity, 0) / hours.length
        const totalPrecip = hours.reduce((s, h) => s + h.precipitation, 0)

        // Dominant weather code: pick the most severe
        const dominantCode = Math.max(...hours.map((h) => h.weatherCode))

        return {
          label,
          dateLabel,
          hours,
          avgTemp,
          maxWind,
          avgHumidity,
          totalPrecip,
          dominantCode,
        }
      },
    )

    const alerts = generateAlerts(blocks)

    return { blocks, alerts }
  } catch (error) {
    console.error('Failed to fetch weather forecast:', error)
    return null
  }
}
